import { useState, useEffect, useCallback } from 'react'
import { GitBranch, RefreshCw, Search, ExternalLink } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

const TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'release', label: 'Release' },
  { value: 'security', label: 'Security' },
  { value: 'license_change', label: 'License Change' },
  { value: 'news', label: 'News' },
]

const TYPE_BADGE: Record<string, string> = {
  release: 'badge-green',
  security: 'badge-red',
  license_change: 'badge-amber',
  news: 'badge-blue',
}

function relativeTime(ts: string): string {
  if (!ts) return ''
  try { return formatDistanceToNow(new Date(ts), { addSuffix: false }) + ' ago' } catch { return ts }
}

export default function OpenSourcePage() {
  const [items, setItems] = useState<any[]>([])
  const [typeFilter, setTypeFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const params: Record<string, any> = { limit: 50 }
      if (typeFilter !== 'all') params.type = typeFilter
      const { data } = await api.get('/api/opensource/feed', { params })
      setItems(data.items || [])
    } finally {
      setLoading(false)
    }
  }, [typeFilter])

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 120_000); return () => clearInterval(i) }, [fetchData])

  const filtered = items.filter(item => {
    if (searchTerm) {
      const q = searchTerm.toLowerCase()
      if (
        !(item.title || '').toLowerCase().includes(q) &&
        !(item.summary || '').toLowerCase().includes(q)
      ) return false
    }
    return true
  })

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <GitBranch size={24} className="text-emerald" /> Open Source Intelligence
          </h1>
          <p className="text-sm text-text-secondary mt-1">Releases and security advisories from GitHub, npm, PyPI, OSS Index, and SPDX trackers</p>
        </div>
        <button type="button" onClick={fetchData} className="btn-secondary text-xs">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search open source news..." className="input pl-9 py-2 text-xs" />
        </div>
        <select title="Filter by type" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="input w-auto py-2 text-xs">
          {TYPES.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>

      {loading && !items.length && (
        <div className="flex items-center justify-center h-32">
          <RefreshCw size={20} className="animate-spin text-text-muted" />
          <span className="ml-2 text-text-muted text-sm">Fetching open source feed...</span>
        </div>
      )}

      <div className="space-y-3">
        {filtered.map((item, idx) => (
          <div key={item.id || idx} className="card card-hover p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  {item.type && (
                    <span className={clsx('text-[10px]', TYPE_BADGE[item.type] || 'badge-blue')}>{item.type.replace(/_/g, ' ')}</span>
                  )}
                </div>
                <h3 className="text-sm font-semibold text-text-primary leading-snug">
                  {item.url ? (
                    <a href={item.url} target="_blank" rel="noopener" className="hover:underline">{item.title}</a>
                  ) : item.title}
                </h3>
                <p className="text-xs text-text-secondary mt-1 leading-relaxed line-clamp-2">{item.summary}</p>
                <div className="flex items-center gap-2 mt-2 text-[10px] text-text-muted">
                  <span>{item.source}</span>
                  <span>&middot;</span>
                  <span>{relativeTime(item.timestamp)}</span>
                </div>
              </div>
              {item.url && (
                <a href={item.url} target="_blank" rel="noopener" className="text-text-muted hover:text-text-primary shrink-0">
                  <ExternalLink size={14} />
                </a>
              )}
            </div>
          </div>
        ))}
        {!loading && filtered.length === 0 && (
          <p className="text-xs text-text-muted text-center py-8">No items match your current filters</p>
        )}
      </div>
    </div>
  )
}
